import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { canViewAgencies } from '../../utils/agencyAccess';

// Card 53584 — the honest states for an options list that FAILED to load, as opposed
// to one that is legitimately empty or permission-denied. AdminFiltersModal skips a
// select with zero options by design (its :264 guard, for the 403 case), so a failed
// filter must still hand it ONE option or the control disappears with no explanation.
// The sentinel's value is '' so selecting it filters nothing — it is a label, not a filter.
const FILTER_FAILED_PLACEHOLDER = 'Could not load \u2014 filter unavailable';
const OPTION_LOAD_FAILED_CHOICE = [
  { value: '', label: 'This list failed to load \u2014 close and use Try again' }
];
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import FileUploadZone from '../../components/common/FileUploadZone';
import AdminFiltersModal, {
  FiltersButton,
  FilterChips,
  buildFilterChips,
  countActiveFilters,
  removeFilterValue
} from '../../components/admin/AdminFiltersModal';
import { resolveDateRange } from '../../utils/dateRangePresets';
import { readFiltersFromParams, writeFiltersToParams } from './membersFilterParams';
import api from '../../services/api';
import styles from './MembersPage.module.css';

// Subscription plans data
const subscriptionPlans = [
  { code: 'none', name: 'No Plan' },
  { code: 'access_individual', name: 'ACCESS Individual', color: '#10b981' },
  { code: 'access_spouse', name: 'ACCESS Spouse', color: '#10b981' },
  { code: 'access_family', name: 'ACCESS Family', color: '#10b981' },
  { code: 'access_plus_individual', name: 'ACCESS+ Individual', color: '#14b8a6' },
  { code: 'access_plus_spouse', name: 'ACCESS+ Spouse', color: '#14b8a6' },
  { code: 'access_plus_family', name: 'ACCESS+ Family', color: '#14b8a6' },
  { code: 'one_individual', name: 'ONE Individual', color: '#f97316' },
  { code: 'one_spouse', name: 'ONE Spouse', color: '#f97316' },
  { code: 'one_family', name: 'ONE Family', color: '#22c55e' },
  { code: 'plus', name: 'PLUS', color: '#3b82f6' },
  { code: 'plus_individual', name: 'PLUS Individual', color: '#3b82f6' },
  { code: 'plus_spouse', name: 'PLUS Spouse', color: '#3b82f6' },
  { code: 'plus_family', name: 'PLUS Family', color: '#8b5cf6' },
  { code: 'premium', name: 'Premium', color: '#ec4899' },
  { code: 'premium_spouse', name: 'Premium Spouse', color: '#ec4899' },
  { code: 'premium_family', name: 'Premium Family', color: '#06b6d4' },
  { code: 'enterprise', name: 'Enterprise', color: '#14b8a6' },
  { code: 'trial', name: 'Trial', color: '#6b7280' },
  { code: 'lifetime', name: 'Lifetime', color: '#eab308' },
];

const getPlanByCode = (code) => subscriptionPlans.find(p => p.code === code) || subscriptionPlans[0];

// Urgency levels with colors
const urgencyLevels = {
  low: { label: 'Low', color: '#6b7280', bgColor: '#f3f4f6' },
  medium: { label: 'Medium', color: '#d97706', bgColor: '#fef3c7' },
  high: { label: 'High', color: '#ea580c', bgColor: '#ffedd5' },
  critical: { label: 'Critical', color: '#dc2626', bgColor: '#fee2e2' }
};

const getUrgencyConfig = (urgency) => urgencyLevels[urgency] || urgencyLevels.low;


// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'Washington DC' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

// Validate ZIP code (5 digits)
const isValidZipCode = (zip) => !zip || /^\d{5}$/.test(zip);

// Order status display config
const orderStatusConfig = {
  pending: { label: 'Pending', color: '#737373', bgColor: '#f3f4f6' },
  under_review: { label: 'Under Review', color: '#d97706', bgColor: '#fef3c7' },
  approved: { label: 'Approved', color: '#059669', bgColor: '#ecfdf5' },
  denied: { label: 'Denied', color: '#dc2626', bgColor: '#fee2e2' },
  processing: { label: 'Processing', color: '#2563eb', bgColor: '#eff6ff' },
  shipped: { label: 'Shipped', color: '#7c3aed', bgColor: '#f5f3ff' },
  delivered: { label: 'Delivered', color: '#059669', bgColor: '#ecfdf5' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bgColor: '#f9fafb' }
};

const getOrderStatusConfig = (status) => orderStatusConfig[status] || null;

// Check if subscription plan supports dependents
const tierSupportsDependents = (plan) => {
  if (!plan) return false;
  return plan.includes('spouse') || plan.includes('family');
};

// Format phone number as (XXX) XXX-XXXX
const formatPhone = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length !== 10) return phone;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Consolidated Filters (shared AdminFiltersModal) — canonical select options
const URGENCY_OPTIONS = Object.entries(urgencyLevels).map(([value, cfg]) => ({
  value,
  label: cfg.label
}));
const CALLED_OPTIONS = [
  { value: 'false', label: 'Not Called Yet' },
  { value: 'true', label: 'Already Called' }
];
const ACCOUNT_STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' }
];
const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];
const TEST_OPTIONS = [
  { value: 'true', label: 'Test Only' },
  { value: 'false', label: 'Real Only' }
];

const MembersPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [pagination, setPagination] = useState(null);
  // Card 54621 - the server hides suspected test-data accounts (legacy audit
  // SIG-01..SIG-07) by default and tells us how many it hid. `showFlagged` is the
  // reveal toggle; it is sent as `flagged=show`, the ONLY value that reveals them.
  const [showFlagged, setShowFlagged] = useState(false);
  const [flaggedHiddenCount, setFlaggedHiddenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [customTiers, setCustomTiers] = useState([]);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('clients.edit');
  const canCreate = hasPermission('clients.create');
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    memberId: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
    subscriptionPlan: 'none',
    customMembershipTierId: '',
    agency: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [zipError, setZipError] = useState('');

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadColumnMap, setUploadColumnMap] = useState({ email: -1, firstName: -1, lastName: -1, phone: -1, membershipTier: -1, entity: -1 });
  const [uploadValidation, setUploadValidation] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [standardTiers, setStandardTiers] = useState([]);
  // Card 53584: a FAILED options fetch is not "this system has none". Without these
  // flags the three loaders below left their arrays at [], and AdminFiltersModal's
  // deliberate zero-option skip (AdminFiltersModal.jsx:264, for the 403 case) then
  // REMOVED the tier/custom-tier/agency filters outright — measured 8 selects -> 5.
  // The edit form silently dropped its custom-tier optgroup (35 opts -> 20) and
  // collapsed the Agency picker to the single destructive choice "No Agency".
  const [optionLoadFailed, setOptionLoadFailed] = useState({
    agencies: false, customTiers: false, standardTiers: false
  });
  const markOptionLoad = (key, failed) =>
    setOptionLoadFailed((prev) => (prev[key] === failed ? prev : { ...prev, [key]: failed }));
  const [showResultDetails, setShowResultDetails] = useState(false);

  // URL is the single source of truth for filter state
  const filters = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);

  useEffect(() => {
    loadMembers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    loadAgencies();
    loadCustomTiers();
    loadStandardTiers();
  }, []);

  const loadAgencies = async () => {
    // Gated on the permission the API already enforces (card 49621): without
    // agencies.view this request can only ever 403, and the old catch reported
    // that denial as "no agencies / API unavailable", which is false on both counts.
    if (!canViewAgencies(hasPermission)) {
      setAgencies([]);
      return;
    }
    try {
      const response = await api.get('/admin/agencies');
      setAgencies(response.agencies || []);
      markOptionLoad('agencies', false);
    } catch (error) {
      console.error('[Admin] Failed to load agencies:', error?.message ?? error);
      setAgencies([]);
      markOptionLoad('agencies', true);
    }
  };

  const loadCustomTiers = async () => {
    try {
      const response = await api.get('/admin/custom-membership-tiers');
      setCustomTiers((response.tiers || []).filter(t => t.isActive));
      markOptionLoad('customTiers', false);
    } catch (error) {
      // Card 53584: this used to log "No custom tiers found" on a FETCH FAILURE —
      // the false inference, asserted by the code's own log line.
      console.error('[Admin] Failed to load custom membership tiers:', error?.message ?? error);
      setCustomTiers([]);
      markOptionLoad('customTiers', true);
    }
  };

  const loadStandardTiers = async () => {
    try {
      const response = await api.get('/admin/membership-tiers');
      setStandardTiers((response.tiers || []).filter(t => t.isActive));
      markOptionLoad('standardTiers', false);
    } catch (error) {
      // Card 53584: this used to log "No standard tiers found" on a FETCH FAILURE.
      console.error('[Admin] Failed to load membership tiers:', error?.message ?? error);
      setStandardTiers([]);
      markOptionLoad('standardTiers', true);
    }
  };

  // Card 53584 — re-runs only the three option loaders, so a transient failure is
  // recoverable in place. Each loader clears its own flag on success.
  const retryOptionLoads = () => {
    loadAgencies();
    loadCustomTiers();
    loadStandardTiers();
  };
  const anyOptionLoadFailed =
    optionLoadFailed.agencies || optionLoadFailed.customTiers || optionLoadFailed.standardTiers;

  const autoDetectColumn = (headers, patterns) => {
    const idx = headers.findIndex(h =>
      patterns.some(p => h.toLowerCase().replace(/[_\s-]/g, '').includes(p))
    );
    return idx >= 0 ? idx : -1;
  };

  const handleUploadFileSelect = (file) => {
    setUploadFile(file);
    setUploadResults(null);
    if (!file) {
      setUploadPreview(null);
      setUploadColumnMap({ email: -1, firstName: -1, lastName: -1, phone: -1, membershipTier: -1, entity: -1 });
      setUploadValidation(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length < 2) {
        setUploadPreview(null);
        setUploadValidation({ valid: 0, invalid: 0, duplicates: 0, total: 0 });
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const rows = lines.slice(1).map(l => {
        const row = [];
        let current = '';
        let inQuotes = false;
        for (const ch of l) {
          if (ch === '"') { inQuotes = !inQuotes; }
          else if (ch === ',' && !inQuotes) { row.push(current.trim()); current = ''; }
          else { current += ch; }
        }
        row.push(current.trim());
        return row;
      });

      const colMap = {
        email: autoDetectColumn(headers, ['email', 'emailaddress', 'mail']),
        firstName: autoDetectColumn(headers, ['firstname', 'first', 'fname', 'givenname']),
        lastName: autoDetectColumn(headers, ['lastname', 'last', 'lname', 'surname', 'familyname']),
        phone: autoDetectColumn(headers, ['phone', 'telephone', 'tel', 'mobile', 'cell', 'phonenumber']),
        membershipTier: autoDetectColumn(headers, ['plancode', 'newplancode', 'membershipplan', 'membershiptier', 'plan', 'tier']),
        entity: autoDetectColumn(headers, ['entity', 'agencyname', 'agency', 'tpaname', 'tpa', 'organization', 'org']),
      };

      setUploadColumnMap(colMap);
      setUploadPreview({ headers, rows });

      const emailIdx = colMap.email;
      const emails = new Set();
      let valid = 0, invalid = 0, duplicates = 0;
      rows.forEach(row => {
        const email = emailIdx >= 0 ? (row[emailIdx] || '').toLowerCase().trim() : '';
        if (!email || !email.includes('@')) { invalid++; }
        else if (emails.has(email)) { duplicates++; }
        else { emails.add(email); valid++; }
      });
      setUploadValidation({ valid, invalid, duplicates, total: rows.length });
    };
    reader.readAsText(file);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) return;
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadColumnMap.membershipTier >= 0) formData.append('membershipTierIdx', uploadColumnMap.membershipTier);
      if (uploadColumnMap.entity >= 0) formData.append('entityIdx', uploadColumnMap.entity);
      const response = await api.upload('/admin/members/upload', formData);
      setUploadResults(response);
      loadMembers();
      const summary = `Upload complete: ${response.created} created, ${response.updated || 0} changed, ${response.skipped} skipped`;
      // The API spreads a non-fatal `uploadRecordWarning` into an otherwise-successful response when
      // the member_uploads audit row is refused (memberController.js:1792). The members WERE imported,
      // so this stays non-blocking -- but an unqualified success toast would hide the one signal that
      // the admin's audit trail is incomplete.
      if (response.uploadRecordWarning) {
        toast.warning(`${summary} - but its Upload History record was not saved`);
      } else {
        toast.success(summary);
      }
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const resetUploadModal = () => {
    setUploadFile(null);
    setUploadPreview(null);
    setUploadColumnMap({ email: -1, firstName: -1, lastName: -1, phone: -1, membershipTier: -1, entity: -1 });
    setUploadValidation(null);
    setUploadResults(null);
    setUploadLoading(false);
    setShowResultDetails(false);
  };

  const handleOpenUploadModal = () => {
    resetUploadModal();
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  const loadMembers = async (page = 1, searchTerm = search, showFlaggedOverride) => {
    const revealFlagged = showFlaggedOverride === undefined ? showFlagged : showFlaggedOverride;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchTerm) params.append('search', searchTerm);
      if (filters.urgency) params.append('urgency', filters.urgency);
      if (filters.called) params.append('hasBeenCalled', filters.called);
      if (filters.active) params.append('isActive', filters.active);
      // Card 54621 - the operational directory now hides EVERY suspected test-data
      // account the legacy audit flags (SIG-01..SIG-07), not just metadata.is_test.
      // The server applies that by default, so this page stops forcing `test=false`
      // on every request; the reveal toggle asks for `flagged=show`. Client type ->
      // Test Only still sends its own legacy `test=true`, which the server honours.
      if (revealFlagged) {
        params.append('flagged', 'show');
      } else if (filters.test) {
        params.append('test', filters.test);
      }
      if (filters.subStatus) params.append('subscriptionStatus', filters.subStatus);
      if (filters.tier) params.append('membershipTier', filters.tier);
      if (filters.customTier) params.append('customTierId', filters.customTier);
      if (filters.agency) params.append('agencyId', filters.agency);
      const range = resolveDateRange(filters.joined);
      if (range) {
        params.append('dateFrom', range.from);
        params.append('dateTo', range.to);
      }

      const response = await api.get(`/admin/members?${params}`);
      setMembers(response.users || []);
      setPagination(response.pagination);
      setFlaggedHiddenCount(response.flaggedHiddenCount ?? 0);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowDropdown(false);
    loadMembers(1, search);
  };

  const filterDefs = useMemo(
    () => [
      { key: 'joined', label: 'Joined', type: 'dateRange' },
      { key: 'urgency', label: 'Urgency', type: 'select', placeholder: 'All urgency', options: URGENCY_OPTIONS },
      { key: 'called', label: 'Called', type: 'select', placeholder: 'All clients', options: CALLED_OPTIONS },
      { key: 'active', label: 'Account status', type: 'select', placeholder: 'All statuses', options: ACCOUNT_STATUS_OPTIONS },
      { key: 'subStatus', label: 'Subscription status', type: 'select', placeholder: 'All statuses', options: SUBSCRIPTION_STATUS_OPTIONS },
      {
        key: 'tier',
        label: optionLoadFailed.standardTiers ? 'Membership tier \u2014 could not load' : 'Membership tier',
        type: 'select',
        placeholder: optionLoadFailed.standardTiers ? FILTER_FAILED_PLACEHOLDER : 'All tiers',
        options: optionLoadFailed.standardTiers
          ? OPTION_LOAD_FAILED_CHOICE
          : standardTiers.map((tier) => ({ value: tier.name, label: tier.name }))
      },
      {
        key: 'customTier',
        label: optionLoadFailed.customTiers ? 'Custom tier \u2014 could not load' : 'Custom tier',
        type: 'select',
        placeholder: optionLoadFailed.customTiers ? FILTER_FAILED_PLACEHOLDER : 'All custom tiers',
        options: optionLoadFailed.customTiers
          ? OPTION_LOAD_FAILED_CHOICE
          : customTiers.map((tier) => ({ value: tier.id, label: tier.name }))
      },
      {
        key: 'agency',
        label: optionLoadFailed.agencies ? 'Entity / Agency \u2014 could not load' : 'Entity / Agency',
        type: 'select',
        placeholder: optionLoadFailed.agencies ? FILTER_FAILED_PLACEHOLDER : 'All agencies',
        options: optionLoadFailed.agencies
          ? OPTION_LOAD_FAILED_CHOICE
          : agencies.map((agency) => ({ value: agency.id || agency._id, label: agency.name }))
      },
      { key: 'test', label: 'Client type', type: 'select', placeholder: 'Real clients (default)', options: TEST_OPTIONS }
    ],
    [standardTiers, customTiers, agencies, optionLoadFailed]
  );

  const activeFilterCount = countActiveFilters(filters);
  const filterChips = useMemo(() => buildFilterChips(filterDefs, filters), [filterDefs, filters]);

  const applyFilters = (nextValues) => {
    setSearchParams(writeFiltersToParams(nextValues));
    setFiltersOpen(false);
  };

  const handleRemoveChip = (chip) => {
    applyFilters(removeFilterValue(filters, chip));
  };

  const handleResetFilters = () => {
    applyFilters({});
  };

  // Typeahead search with debounce
  const handleSearchInput = useCallback((value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.trim().length < 2) {
      setSearchResults([]);
      setSearchError(null);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await api.get(`/admin/members/search?q=${encodeURIComponent(value.trim())}`);
        setSearchResults(response.results || []);
        setSearchError(null);
        setShowDropdown(true);
      } catch (err) {
        console.error('Member search failed:', err);
        setSearchResults([]);
        setSearchError(err?.message || 'Search is temporarily unavailable.');
        setShowDropdown(true);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpenModal = (member = null) => {
    setZipError('');
    if (member) {
      setEditingMember(member);
      const addr = member.profile?.address || {};
      setFormData({
        memberId: member.memberId || '',
        email: member.email || '',
        firstName: member.profile?.firstName || '',
        lastName: member.profile?.lastName || '',
        role: member.role || 'user',
        subscriptionPlan: member.subscriptionPlan || 'none',
        customMembershipTierId: member.customMembershipTierId || '',
        agency: member.agency?.id || member.agency?._id || (typeof member.agency === 'string' ? member.agency : ''),
        phone: member.profile?.phone || '',
        street: addr.street || '',
        city: addr.city || '',
        state: addr.state || '',
        zipCode: addr.zipCode || ''
      });
    } else {
      setEditingMember(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user',
        subscriptionPlan: 'none',
        customMembershipTierId: '',
        agency: '',
        phone: '',
        street: '',
        city: '',
        state: '',
        zipCode: ''
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate ZIP code if provided
    if (formData.zipCode && !isValidZipCode(formData.zipCode)) {
      setZipError('ZIP code must be 5 digits');
      return;
    }
    setZipError('');
    
    try {
      // Build address object if any address field is filled
      const hasAddress = formData.street || formData.city || formData.state || formData.zipCode;
      const addressData = hasAddress ? {
        street: formData.street || null,
        city: formData.city || null,
        state: formData.state || null,
        zipCode: formData.zipCode || null
      } : null;
      
      const payload = {
        email: formData.email,
        memberId: formData.memberId || null,
        profile: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          ...(addressData && { address: addressData })
        },
        role: formData.role,
        subscriptionPlan: formData.subscriptionPlan,
        customMembershipTierId: formData.customMembershipTierId || null,
        agency: formData.agency || null
      };

      if (editingMember) {
        await api.patch(`/admin/members/${editingMember._id || editingMember.id}`, payload);
        toast.success('Client updated successfully');
      } else {
        await api.post('/admin/members', payload);
        toast.success('Client created successfully');
      }
      setModalOpen(false);
      loadMembers();
    } catch (error) {
      toast.error(error.message || 'Failed to save client');
    }
  };

  const handleDeactivate = async (memberId) => {
    if (!confirm('Are you sure you want to deactivate this client?')) return;

    try {
      await api.delete(`/admin/members/${memberId}`);
      toast.success('Client deactivated');
      loadMembers();
    } catch (error) {
      toast.error('Failed to deactivate client');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Clients</h1>
          <p>Manage your platform clients</p>
        </div>
        <div className={styles.headerButtons}>
          {canEdit && <Button variant="outline" onClick={handleOpenUploadModal}>Upload CSV</Button>}
          {canCreate && <Button onClick={() => handleOpenModal()}>Add Client</Button>}
        </div>
      </div>

      {/* Search and Filters */}
      <Card className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <div className={styles.searchWrapper} ref={searchRef}>
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => handleSearchInput(e.target.value)}
              onFocus={() => { if (searchResults.length > 0 || searchError) setShowDropdown(true); }}
            />
            {showDropdown && (searchError || searchResults.length > 0) && (
              <div className={styles.searchDropdown}>
                {searchError && (
                  <div className={styles.searchDropdownError} role="alert">
                    Search is unavailable &mdash; we could not check for matches. This is not a &quot;no matches&quot; answer. Please try again.
                  </div>
                )}
                {searchResults.map((r) => (
                  <div
                    key={r.id}
                    className={styles.searchDropdownItem}
                    onClick={() => {
                      setShowDropdown(false);
                      navigate(`/admin/members/${r.id}`);
                    }}
                  >
                    <div className={styles.searchDropdownAvatar}>
                      {(r.firstName?.[0] || r.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div className={styles.searchDropdownInfo}>
                      <span className={styles.searchDropdownName}>
                        {r.firstName || r.lastName ? `${r.firstName} ${r.lastName}`.trim() : 'No Name'}
                      </span>
                      <span className={styles.searchDropdownEmail}>{r.email}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {searchLoading && (
              <div className={styles.searchSpinner} />
            )}
          </div>
          <FiltersButton activeCount={activeFilterCount} onClick={() => setFiltersOpen(true)} />
          <Button type="submit">Search</Button>
        </form>
        {anyOptionLoadFailed && (
          <div className={styles.uploadWarning} role="alert">
            <span>
              Some filter and form lists could not be loaded, so they are not shown.
              This is not the same as having none.
            </span>
            <Button type="button" variant="outline" onClick={retryOptionLoads}>
              Try again
            </Button>
          </div>
        )}
        <FilterChips chips={filterChips} onRemove={handleRemoveChip} />
      </Card>

      <AdminFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filterDefs}
        values={filters}
        onApply={applyFilters}
        onReset={handleResetFilters}
      />

      {/* Card 54621 - what the list is NOT showing, stated rather than left implicit. */}
      {!loading && flaggedHiddenCount > 0 && (
        <div className={styles.testDataBanner} role="status">
          <span>
            <strong>{flaggedHiddenCount.toLocaleString()}</strong> suspected test-data
            {flaggedHiddenCount === 1 ? ' account is' : ' accounts are'} hidden from this list.
          </span>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowFlagged(true);
              loadMembers(1, search, true);
            }}
          >
            Show them
          </Button>
        </div>
      )}
      {!loading && showFlagged && (
        <div className={`${styles.testDataBanner} ${styles.testDataBannerVisible}`} role="status">
          <span>Suspected test-data accounts are shown alongside real clients.</span>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowFlagged(false);
              loadMembers(1, search, false);
            }}
          >
            Hide them again
          </Button>
        </div>
      )}

      {/* Members Table */}
      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Client</th>
                <th className={styles.memberIdCol}>Client ID</th>
                <th>Phone</th>
                <th>Dependents</th>
                <th>Tags</th>
                <th>Role</th>
                <th>Order Status</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member._id || member.id}>
                  <td>
                    <div className={styles.memberInfo}>
                      <div className={styles.avatar}>
                        {member.profile?.firstName?.[0] || member.email[0].toUpperCase()}
                      </div>
                      <div>
                        <div className={styles.nameRow}>
                          <span className={styles.name}>
                            {member.profile?.firstName} {member.profile?.lastName}
                          </span>
                          <span
                            className={styles.calledDot}
                            style={{ backgroundColor: member.calledStatus === 'called' || member.hasBeenCalled ? '#059669' : '#f97316' }}
                            title={member.calledStatus === 'called' || member.hasBeenCalled ? 'Called' : 'Not Called'}
                          />
                          {member.isDemo && (
                            <span className={styles.demoBadge} title="Demo Account">★</span>
                          )}
                          {member.urgency && member.urgency !== 'low' && (
                            <span
                              className={`${styles.urgencyBadge} ${member.urgency === 'critical' ? styles.urgencyPulse : ''}`}
                              style={{
                                backgroundColor: getUrgencyConfig(member.urgency).bgColor,
                                color: getUrgencyConfig(member.urgency).color
                              }}
                            >
                              {getUrgencyConfig(member.urgency).label}
                            </span>
                          )}
                        </div>
                        <div className={styles.email}>{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className={styles.memberIdCell}>
                    <span
                      className={styles.memberId}
                      onClick={() => {
                        if (member.memberId) {
                          navigator.clipboard.writeText(member.memberId);
                          toast.success("Client ID copied");
                        }
                      }}
                      title={member.memberId ? "Click to copy Client ID" : ""}
                      style={member.memberId ? { cursor: 'pointer' } : { cursor: 'default' }}
                    >
                      {member.memberId || "—"}
                    </span>
                  </td>
                  <td className={styles.phoneCell}>
                    {member.profile?.phone ? (
                      <span className={styles.phone}>{formatPhone(member.profile.phone)}</span>
                    ) : (
                      <span className={styles.noPhone}>—</span>
                    )}
                  </td>
                  <td className={styles.dependentsCell}>
                    {tierSupportsDependents(member.subscriptionPlan) ? (
                      <span
                        className={styles.dependentCount}
                        onClick={() => navigate(`/admin/members/${member._id || member.id}#dependents`)}
                        title="View dependents"
                      >
                        {member.dependentCount || 0}
                      </span>
                    ) : (
                      <span className={styles.noDependents}>—</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.tagsContainer}>
                      {member.agency && (
                        <span
                          className={styles.agencyBadge}
                          style={{ backgroundColor: member.agency.color || '#6366f1' }}
                          title={`Agency: ${member.agency.name}`}
                        >
                          {member.agency.name}
                        </span>
                      )}
                      {member.customMembershipTierId && (
                        <span
                          className={styles.planBadge}
                          style={{ backgroundColor: '#7c3aed' }}
                        >
                          Custom Tier
                        </span>
                      )}
                      {!member.customMembershipTierId && member.subscriptionPlan && member.subscriptionPlan !== 'none' && (
                        <span
                          className={styles.planBadge}
                          style={{ backgroundColor: getPlanByCode(member.subscriptionPlan).color }}
                        >
                          {getPlanByCode(member.subscriptionPlan).name}
                        </span>
                      )}
                      {!member.agency && (!member.subscriptionPlan || member.subscriptionPlan === 'none') && (
                        <span className={styles.noPlan}>—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.badge} ${styles[member.role] || ''}`}>
                      {member.role}
                    </span>
                  </td>
                  <td>
                    {member.latestOrderStatus ? (
                      <span
                        className={styles.orderStatusBadge}
                        style={{
                          backgroundColor: getOrderStatusConfig(member.latestOrderStatus)?.bgColor,
                          color: getOrderStatusConfig(member.latestOrderStatus)?.color
                        }}
                      >
                        {getOrderStatusConfig(member.latestOrderStatus)?.label || member.latestOrderStatus}
                      </span>
                    ) : (
                      <span className={styles.noOrder}>—</span>
                    )}
                  </td>
                  <td>
                    {/* Card 30787. A member booked for a future effective date is persisted as an
                        ordinary INACTIVE user plus a `pending` member_activation_schedule row, so
                        the three chips below render "not a member YET" identically to "membership
                        ended". This branch is the only thing that tells them apart; it is purely
                        additive and `activationState` is null for every member without a row, so
                        the Active/Inactive/Deactivated arms are reached exactly as before. */}
                    {member.activationState === 'pending' ? (
                      <div className={styles.activationCell}>
                        <span className={`${styles.status} ${styles.pending}`}>
                          Pending
                        </span>
                        {member.effectiveDate && (
                          <span className={styles.effectiveDate}>
                            Effective {member.effectiveDate}
                          </span>
                        )}
                      </div>
                    ) : !member.isActive ? (
                      <span className={`${styles.status} ${styles.deactivated}`}>
                        Deactivated
                      </span>
                    ) : (member.lastLogin || member.subscriptionStatus === 'active') ? (
                      <span className={`${styles.status} ${styles.active}`}>
                        Active
                      </span>
                    ) : (
                      <span className={`${styles.status} ${styles.inactive}`}>
                        Inactive
                      </span>
                    )}
                  </td>
                  <td>{new Date(member.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className={styles.actions}>

                      <button
                        className={styles.actionBtn}
                        onClick={() => navigate(`/admin/members/${member._id || member.id}`)}
                      >
                        View
                      </button>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleOpenModal(member)}
                      >
                        Edit
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.danger}`}
                        onClick={() => handleDeactivate(member._id || member.id)}
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && members.length === 0 && (
          <div className={styles.empty}>No members found</div>
        )}
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="outline"
            size="small"
            disabled={!pagination.hasPrevPage}
            onClick={() => loadMembers(pagination.page - 1, search)}
          >
            Previous
          </Button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <Button
            variant="outline"
            size="small"
            disabled={!pagination.hasNextPage}
            onClick={() => loadMembers(pagination.page + 1, search)}
          >
            Next
          </Button>
        </div>
      )}


      {/* Upload History lives in Data Imports (?tab=upload-history) — card 6237 */}
      <div className={styles.uploadHistoryNote}>
        <span>Client upload history has moved to Data Imports.</span>
        <Link className={styles.uploadHistoryLink} to="/admin/data-imports?tab=upload-history">
          View Upload History
        </Link>
      </div>

      {/* Create/Edit Member Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingMember ? 'Edit Client' : 'Add Client'}
        size="medium"
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
<div className={styles.formGroup}>            <label className={styles.label}>Client ID</label>            <Input              value={formData.memberId}              onChange={(e) => setFormData(prev => ({ ...prev, memberId: e.target.value }))}              placeholder="e.g. MEM-001"            />          </div>
            <label className={styles.label}>Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="member@example.com"
              required
              disabled={!!editingMember}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>First Name</label>
              <Input
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="First name"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Last Name</label>
              <Input
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Last name"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Phone</label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="5551234567"
            />
          </div>

          {/* Address Section */}
          <div className={styles.addressSection}>
            <label className={styles.sectionLabel}>Address</label>
            <div className={styles.formGroup}>
              <Input
                value={formData.street}
                onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                placeholder="Street address"
              />
            </div>
            <div className={styles.addressRow}>
              <div className={styles.formGroup}>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                />
              </div>
              <div className={styles.formGroup}>
                <select
                  className={styles.select}
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                >
                  <option value="">State</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <Input
                  value={formData.zipCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFormData(prev => ({ ...prev, zipCode: val }));
                    if (val && !isValidZipCode(val)) {
                      setZipError('5 digits required');
                    } else {
                      setZipError('');
                    }
                  }}
                  placeholder="ZIP"
                  maxLength={5}
                  className={zipError ? styles.inputError : ''}
                />
                {zipError && <span className={styles.errorText}>{zipError}</span>}
              </div>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Role</label>
              <select
                className={styles.select}
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Membership Tier</label>
              <select
                className={styles.select}
                value={formData.subscriptionPlan}
                onChange={(e) => setFormData(prev => ({ ...prev, subscriptionPlan: e.target.value }))}
              >
                {subscriptionPlans.map((plan) => (
                  <option key={plan.code} value={plan.code}>
                    {plan.name}
                  </option>
                ))}
                {optionLoadFailed.customTiers && (
                  <option value="" disabled>
                    {'\u2014 Custom tiers could not be loaded \u2014'}
                  </option>
                )}
                {customTiers.length > 0 && (
                  <optgroup label="── Custom Tiers ──">
                    {customTiers.map((tier) => (
                      <option key={'custom_' + tier.id} value={'custom_' + tier.id}>
                        ★ {tier.name} (Custom)
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Agency</label>
            {optionLoadFailed.agencies && (
              <p className={styles.errorText} role="alert">
                The agency list failed to load, so this field is locked and will be saved
                unchanged. Close this dialog and press Try again.
              </p>
            )}
            <select
              className={styles.select}
              value={formData.agency}
              disabled={optionLoadFailed.agencies}
              onChange={(e) => setFormData(prev => ({ ...prev, agency: e.target.value }))}
            >
              {optionLoadFailed.agencies ? (
                <option value={formData.agency}>
                  {'Agency list could not be loaded \u2014 unchanged'}
                </option>
              ) : (
                <option value="">No Agency</option>
              )}
              {agencies.map((agency) => (
                <option key={agency._id || agency.id} value={agency._id || agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingMember ? 'Update Client' : 'Create Client'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Upload CSV Modal */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={handleCloseUploadModal}
        title="Upload Clients CSV"
        size="large"
      >
        <div className={styles.uploadModal}>
          {uploadResults ? (
            <div className={styles.uploadResults}>
              <div className={styles.resultsSummary}>
                <div className={styles.resultStat}>
                  <span className={styles.resultCount} style={{ color: '#059669' }}>{uploadResults.created}</span>
                  <span className={styles.resultLabel}>Created</span>
                </div>
                <div className={styles.resultStat}>
                  <span className={styles.resultCount} style={{ color: '#2563eb' }}>{uploadResults.updated || 0}</span>
                  <span className={styles.resultLabel}>Changed</span>
                </div>
                <div className={styles.resultStat}>
                  <span className={styles.resultCount} style={{ color: '#d97706' }}>{uploadResults.skipped}</span>
                  <span className={styles.resultLabel}>Skipped</span>
                </div>
                <div className={styles.resultStat}>
                  <span className={styles.resultCount} style={{ color: '#dc2626' }}>{uploadResults.failed}</span>
                  <span className={styles.resultLabel}>Failed</span>
                </div>
              </div>

              {uploadResults.uploadRecordWarning && (
                <div className={styles.uploadWarning} role="status">
                  {uploadResults.uploadRecordWarning}
                </div>
              )}

              <button
                type="button"
                className={styles.detailsToggle}
                onClick={() => setShowResultDetails(!showResultDetails)}
              >
                {showResultDetails ? 'Hide Details' : 'Show Details'}
              </button>

              {showResultDetails && uploadResults.results && (
                <div className={styles.resultDetails}>
                  {uploadResults.results.map((r, i) => (
                    <div key={i} className={styles.resultRow}>
                      <span className={styles.resultEmail}>{r.email}</span>
                      <span className={`${styles.resultBadge} ${styles[`result_${r.status}`]}`}>{r.status}</span>
                      {r.reason && <span className={styles.resultReason}>{r.reason}</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.formActions}>
                <Button onClick={handleCloseUploadModal}>Done</Button>
              </div>
            </div>
          ) : (
            <div className={styles.uploadForm}>
              <div className={styles.uploadSection}>
                <label className={styles.sectionLabel}>CSV File</label>
                <FileUploadZone
                  accept=".csv"
                  onFileSelect={handleUploadFileSelect}
                  placeholder="Drop your CSV file here or click to browse"
                  hint="Accepted format: .csv with email column required"
                />
              </div>

              {uploadPreview && (
                <div className={styles.uploadSection}>
                  <label className={styles.sectionLabel}>Preview (first 5 rows)</label>
                  <div className={styles.previewTableWrap}>
                    <table className={styles.previewTable}>
                      <thead>
                        <tr>
                          {uploadPreview.headers.map((h, i) => (
                            <th key={i}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {uploadPreview.rows.slice(0, 5).map((row, ri) => (
                          <tr key={ri}>
                            {row.map((cell, ci) => (
                              <td key={ci}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {uploadPreview && (
                <div className={styles.uploadSection}>
                  <label className={styles.sectionLabel}>Column Mapping</label>
                  <div className={styles.columnMapping}>
                    {['email', 'firstName', 'lastName', 'phone', 'membershipTier', 'entity'].map((field) => (
                      <div key={field} className={styles.columnMapRow}>
                        <span className={styles.columnMapLabel}>
                          {field === 'firstName' ? 'First Name' : field === 'lastName' ? 'Last Name' : field === 'membershipTier' ? 'Membership Tier' : field === 'entity' ? 'Entity' : field.charAt(0).toUpperCase() + field.slice(1)}
                          {field === 'email' && <span className={styles.required}> *</span>}
                        </span>
                        <select
                          className={styles.columnMapSelect}
                          value={uploadColumnMap[field]}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setUploadColumnMap(prev => ({ ...prev, [field]: val }));
                            if (field === 'email' && uploadPreview) {
                              const emailIdx = val;
                              const emails = new Set();
                              let valid = 0, invalid = 0, duplicates = 0;
                              uploadPreview.rows.forEach(row => {
                                const email = emailIdx >= 0 ? (row[emailIdx] || '').toLowerCase().trim() : '';
                                if (!email || !email.includes('@')) { invalid++; }
                                else if (emails.has(email)) { duplicates++; }
                                else { emails.add(email); valid++; }
                              });
                              setUploadValidation({ valid, invalid, duplicates, total: uploadPreview.rows.length });
                            }
                          }}
                        >
                          <option value={-1}>-- Not mapped --</option>
                          {uploadPreview.headers.map((h, i) => (
                            <option key={i} value={i}>{h}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  <p className={styles.columnMapTip}>Tip: If Membership Tier is mapped, tier will be auto-detected from CSV data. If not mapped, clients default to ACCESS tier.</p>
                </div>
              )}

              {uploadValidation && (
                <div className={styles.uploadSection}>
                  <label className={styles.sectionLabel}>Validation</label>
                  <div className={styles.validationSummary}>
                    <span className={styles.validCount}>{uploadValidation.valid} valid</span>
                    {uploadValidation.invalid > 0 && (
                      <span className={styles.invalidCount}>{uploadValidation.invalid} missing email</span>
                    )}
                    {uploadValidation.duplicates > 0 && (
                      <span className={styles.duplicateCount}>{uploadValidation.duplicates} duplicates</span>
                    )}
                  </div>
                </div>
              )}

              <div className={styles.formActions}>
                <Button type="button" variant="outline" onClick={handleCloseUploadModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadSubmit}
                  disabled={!uploadFile || uploadLoading || (uploadValidation && uploadValidation.valid === 0)}
                >
                  {uploadLoading ? 'Uploading...' : 'Upload Clients'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MembersPage;
